'use es6';

import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import '../listeners/focusStylesListener';
import classNames from 'classnames';
import I18n from 'I18n';
import PropTypes from 'prop-types';
import React, { Children, useEffect } from 'react';
import { LINK_USES, SIZE_CLASSES, USE_CLASSES } from '../button/ButtonConstants';
import ShareButton from '../decorators/ShareButton';
import useActive from '../hooks/useActive';
import UIIcon from '../icon/UIIcon';
import { setupStyledComponents } from '../listeners/setupStyledComponents';
import UITruncateString from '../text/UITruncateString';
import { getRel, getTarget, renderSecurityTaggedLink, warnIfShouldBeExternal } from '../utils/Links';
import { hidden } from '../utils/propTypes/decorators';
import refObject from '../utils/propTypes/refObject';
import { propTypeForSizes, toShorthandSize } from '../utils/propTypes/tshirtSize';
import validHref from '../utils/propTypes/validHref';
import { getTabIndex } from '../utils/TabIndex';
import { getSafeUrl } from '../utils/UnsafeUrl';
import UIClickable from './UIClickable';
setupStyledComponents();

var getUse = function getUse(use) {
  return use && USE_CLASSES[use] ? use : 'secondary';
};

var renderContents = function renderContents(children) {
  var stringBuffer = '';
  var nextChildIndex = 0;
  var childArr = Children.toArray(children);
  return Children.map(children, function (child) {
    nextChildIndex += 1;

    if (child == null) {
      return null;
    } // in response to a breaking change in React 0.15
    // (https://facebook.github.io/react/blog/2016/04/07/react-v15.html#no-more-extra-ltspangts)
    // this wraps any bare strings within a <span> to provide a more
    // predictable DOM for applying automatic spacing rules.


    if (typeof child === 'string') {
      stringBuffer += child;

      if (childArr.length < nextChildIndex || typeof childArr[nextChildIndex] !== 'string') {
        var wrappedStringBuffer = React.createElement("span", null, stringBuffer);
        stringBuffer = '';
        return wrappedStringBuffer;
      } else {
        return null;
      }
    }

    return child;
  });
};

var renderExternalButtonIcon = function renderExternalButtonIcon(external, iconProps) {
  if (!external) return null; // Opening links in a new window is an accessibility anti-pattern.
  // To mitigate this, we add accessible text in the icon to warn that
  // the link will be opened in a new window
  // @link http://diveintoaccessibility.info/day_16_not_opening_new_windows.html

  return React.createElement(UIIcon, Object.assign({}, iconProps, {
    "aria-label": I18n.text('salesUI.UILink.externalTitle'),
    className: iconProps && iconProps.className || 'private-link--external__icon',
    name: "externalLink"
  }));
};

var renderTruncatedContents = function renderTruncatedContents(truncate, truncateStringProps, truncateTooltip, renderedContents, renderedExternalButtonIcon) {
  return truncate ? React.createElement(UITruncateString, Object.assign({
    innerClassName: "private-button__truncate-string",
    useFlex: true,
    fixedChildren: renderedExternalButtonIcon,
    tooltip: truncateTooltip || renderedContents
  }, truncateStringProps), React.createElement("span", null, renderedContents)) : renderedContents;
};

var UIButton = function UIButton(props) {
  var _useNativeButton = props._useNativeButton,
      ariaPressed = props['aria-pressed'],
      __active = props.active,
      autoFocus = props.autoFocus,
      block = props.block,
      buttonRef = props.buttonRef,
      children = props.children,
      className = props.className,
      disabled = props.disabled,
      external = props.external,
      href = props.href,
      iconProps = props.iconProps,
      onClick = props.onClick,
      rel = props.rel,
      responsive = props.responsive,
      size = props.size,
      tabIndex = props.tabIndex,
      target = props.target,
      truncate = props.truncate,
      truncateStringProps = props.truncateStringProps,
      truncateTooltip = props.truncateTooltip,
      type = props.type,
      use = props.use,
      rest = _objectWithoutProperties(props, ["_useNativeButton", "aria-pressed", "active", "autoFocus", "block", "buttonRef", "children", "className", "disabled", "external", "href", "iconProps", "onClick", "rel", "responsive", "size", "tabIndex", "target", "truncate", "truncateStringProps", "truncateTooltip", "type", "use"]);

  var _useActive = useActive(props),
      active = _useActive.active,
      onMouseDown = _useActive.onMouseDown,
      onKeyDown = _useActive.onKeyDown,
      onKeyUp = _useActive.onKeyUp,
      onBlur = _useActive.onBlur;

  useEffect(function () {
    // Simulate autoFocus when rendering a link element
    if (autoFocus && href) {
      buttonRef.current.focus();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  var handleClick = function handleClick(evt) {
    // Block click events while disabled, since we don't actually use [disabled] in the DOM.
    if (disabled) {
      evt.preventDefault();
      evt.stopPropagation();
    } else {
      if (onClick) onClick(evt);
    }
  };

  var handleKeyDown = function handleKeyDown(evt) {
    if (onKeyDown) onKeyDown(evt); // Ignore keydown events that bubbled up

    if (evt.target !== buttonRef.current) return; // If this is an <a> with a click handler, simulate click on Spacebar to be more button-like

    if (onClick && href) {
      if (evt.key === ' ') {
        // Prevent scrolling
        evt.preventDefault();

        if (disabled) {
          evt.stopPropagation();
        } else {
          onClick(evt);
        }
      }
    }
  };

  var shorthandSize = toShorthandSize(size);
  var computedTabIndex = getTabIndex(disabled, tabIndex);
  var computedTarget = getTarget(external, target);
  var computedUse = getUse(use);

  if (process.env.NODE_ENV !== 'production') {
    warnIfShouldBeExternal(external, computedTarget, 'UIButton');
  }

  var renderedContents = renderContents(children);
  var renderedExternalButtonIcon = renderExternalButtonIcon(external, iconProps);
  var wrappedContents = renderTruncatedContents(truncate, truncateStringProps, truncateTooltip, renderedContents, renderedExternalButtonIcon);
  var unstyled = computedUse === 'unstyled';
  var computedClassName = unstyled ? classNames(className, USE_CLASSES[computedUse]) : classNames('uiButton private-button', USE_CLASSES[computedUse], SIZE_CLASSES[shorthandSize] || SIZE_CLASSES.default, className, active && 'private-button--active', block && 'private-button--block', disabled && 'disabled private-button--disabled', !responsive && 'private-button--non-responsive', LINK_USES.indexOf(computedUse) === -1 && 'private-button--non-link');
  var commonProps = Object.assign({}, rest, {
    'aria-disabled': disabled,
    className: computedClassName,
    'data-button-use': computedUse,
    onBlur: onBlur,
    onClick: handleClick,
    onKeyDown: handleKeyDown,
    onKeyUp: onKeyUp,
    onMouseDown: onMouseDown,
    ref: buttonRef,
    tabIndex: computedTabIndex
  });

  if (href) {
    var computedRel = getRel(rel, computedTarget);
    return renderSecurityTaggedLink(href, React.createElement("a", Object.assign({}, commonProps, {
      href: getSafeUrl(href),
      rel: computedRel,
      target: computedTarget
    }), wrappedContents, truncate || renderedExternalButtonIcon));
  }

  var ButtonComponent = _useNativeButton ? 'button' : UIClickable;

  if (!_useNativeButton) {
    // Pass buttonRef down directly to the UIClickable
    commonProps.buttonRef = buttonRef;
    delete commonProps.ref;
  }

  return React.createElement(ButtonComponent, Object.assign({}, commonProps, {
    type: type,
    "aria-pressed": ariaPressed != null ? ariaPressed : active || undefined,
    autoFocus: autoFocus,
    disabled: _useNativeButton ? undefined : disabled,
    onKeyDown: onKeyDown
  }), wrappedContents);
};

UIButton.propTypes = {
  _useNativeButton: PropTypes.bool,
  active: hidden(PropTypes.bool),
  autoFocus: PropTypes.bool,
  'aria-pressed': PropTypes.bool,
  block: PropTypes.bool,
  buttonRef: refObject,
  children: PropTypes.node,
  disabled: PropTypes.bool.isRequired,
  external: PropTypes.bool.isRequired,
  hovered: hidden(PropTypes.bool),
  href: validHref,
  iconProps: PropTypes.object,
  responsive: PropTypes.bool.isRequired,
  rel: PropTypes.string,
  size: propTypeForSizes(['xs', 'sm', 'md'], ['default']),
  target: PropTypes.string,
  truncate: PropTypes.bool.isRequired,
  truncateStringProps: PropTypes.object,
  truncateTooltip: PropTypes.node,
  type: PropTypes.oneOf(['button', 'reset', 'submit']).isRequired,
  use: PropTypes.oneOf(Object.keys(USE_CLASSES)).isRequired
};
UIButton.defaultProps = {
  _useNativeButton: true,
  disabled: false,
  external: false,
  responsive: true,
  size: 'md',
  truncate: false,
  type: 'button',
  use: 'secondary'
};
UIButton.displayName = 'UIButton';
export default ShareButton(UIButton);